// collegeService.js
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

// College status
export const COLLEGE_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending",
  SUSPENDED: "suspended",
};

// Internship types
export const INTERNSHIP_TYPES = {
  FULL_TIME: "full_time",
  PART_TIME: "part_time",
  REMOTE: "remote",
  ON_SITE: "on_site",
  HYBRID: "hybrid",
};

// Agreement types
export const AGREEMENT_TYPES = {
  MOU: "mou", // Memorandum of Understanding
  CONTRACT: "contract",
  INFORMAL: "informal",
  PARTNERSHIP: "partnership",
};

const COLLEGES_COLLECTION = "colleges";

// Add new college
export const addCollege = async (collegeData) => {
  try {
    const college = {
      ...collegeData,
      status: COLLEGE_STATUS.ACTIVE,
      studentsCount: 0,
      internCount: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      isActive: true,
    };

    const docRef = await addDoc(collection(db, COLLEGES_COLLECTION), college);

    toast.success("College added successfully!");
    return { id: docRef.id, ...college };
  } catch (error) {
    console.error("Error adding college:", error);
    toast.error("Failed to add college");
    throw error;
  }
};

// Get all colleges
export const getAllColleges = async (includeInactive = false) => {
  try {
    let q;
    
    if (includeInactive) {
      q = query(
        collection(db, COLLEGES_COLLECTION),
        orderBy("createdAt", "desc")
      );
    } else {
      q = query(
        collection(db, COLLEGES_COLLECTION),
        where("isActive", "==", true),
        orderBy("createdAt", "desc")
      );
    }

    const querySnapshot = await getDocs(q);
    const colleges = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      colleges.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        agreementStartDate: data.agreementStartDate?.toDate(),
        agreementEndDate: data.agreementEndDate?.toDate(),
      });
    });

    return colleges;
  } catch (error) {
    console.error("Error fetching colleges:", error);
    toast.error("Failed to fetch colleges");
    return [];
  }
};

// Get college by ID
export const getCollegeById = async (collegeId) => {
  try {
    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);
    const collegeSnap = await getDoc(collegeRef);

    if (collegeSnap.exists()) {
      const data = collegeSnap.data();
      return {
        id: collegeSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        agreementStartDate: data.agreementStartDate?.toDate(),
        agreementEndDate: data.agreementEndDate?.toDate(),
      };
    }

    return null;
  } catch (error) {
    console.error("Error fetching college:", error);
    toast.error("Failed to fetch college details");
    return null;
  }
};

// Update college
export const updateCollege = async (collegeId, updateData) => {
  try {
    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);

    const updatedData = {
      ...updateData,
      updatedAt: Timestamp.now(),
    };

    await updateDoc(collegeRef, updatedData);

    toast.success("College updated successfully!");
    return true;
  } catch (error) {
    console.error("Error updating college:", error);
    toast.error("Failed to update college");
    throw error;
  }
};

// Delete college (soft delete)
export const deleteCollege = async (collegeId) => {
  try {
    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);

    await updateDoc(collegeRef, {
      isActive: false,
      status: COLLEGE_STATUS.INACTIVE,
      deletedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    toast.success("College deleted successfully!");
    return true;
  } catch (error) {
    console.error("Error deleting college:", error);
    toast.error("Failed to delete college");
    throw error;
  }
};

// Assign student to college
export const assignStudentToCollege = async (collegeId, studentData) => {
  try {
    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);
    const college = await getCollegeById(collegeId);

    if (!college) {
      throw new Error("College not found");
    }

    // Check if student is already assigned
    const existingStudents = college.assignedStudents || [];
    const isAlreadyAssigned = existingStudents.some(
      (student) => student.userId === studentData.userId
    );

    if (isAlreadyAssigned) {
      throw new Error("Student is already assigned to this college");
    }

    const studentAssignment = {
      ...studentData,
      assignedAt: Timestamp.now(),
      status: "active",
    };

    await updateDoc(collegeRef, {
      assignedStudents: arrayUnion(studentAssignment),
      studentsCount: (college.studentsCount || 0) + 1,
      updatedAt: Timestamp.now(),
    });

    toast.success("Student assigned to college successfully!");
    return true;
  } catch (error) {
    console.error("Error assigning student to college:", error);
    toast.error(error.message || "Failed to assign student to college");
    throw error;
  }
};

// Remove student from college
export const removeStudentFromCollege = async (collegeId, studentUserId) => {
  try {
    const college = await getCollegeById(collegeId);

    if (!college) {
      throw new Error("College not found");
    }

    const existingStudents = college.assignedStudents || [];
    const studentToRemove = existingStudents.find(
      (student) => student.userId === studentUserId
    );

    if (!studentToRemove) {
      throw new Error("Student not found in this college");
    }

    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);

    await updateDoc(collegeRef, {
      assignedStudents: arrayRemove(studentToRemove),
      studentsCount: Math.max((college.studentsCount || 1) - 1, 0),
      updatedAt: Timestamp.now(),
    });

    toast.success("Student removed from college successfully!");
    return true;
  } catch (error) {
    console.error("Error removing student from college:", error);
    toast.error(error.message || "Failed to remove student from college");
    throw error;
  }
};

// Update student status in college
export const updateStudentStatusInCollege = async (collegeId, studentUserId, newStatus) => {
  try {
    const college = await getCollegeById(collegeId);

    if (!college) {
      throw new Error("College not found");
    }

    const existingStudents = college.assignedStudents || [];
    const updatedStudents = existingStudents.map((student) => {
      if (student.userId === studentUserId) {
        return {
          ...student,
          status: newStatus,
          statusUpdatedAt: Timestamp.now(),
        };
      }
      return student;
    });

    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);

    await updateDoc(collegeRef, {
      assignedStudents: updatedStudents,
      updatedAt: Timestamp.now(),
    });

    toast.success("Student status updated successfully!");
    return true;
  } catch (error) {
    console.error("Error updating student status:", error);
    toast.error(error.message || "Failed to update student status");
    throw error;
  }
};

// Get colleges with student assignments
export const getCollegesWithStudents = async () => {
  try {
    const colleges = await getAllColleges();
    
    // Filter colleges that have assigned students
    const collegesWithStudents = colleges.filter(
      college => college.assignedStudents && college.assignedStudents.length > 0
    );

    return collegesWithStudents;
  } catch (error) {
    console.error("Error fetching colleges with students:", error);
    return [];
  }
};

// Get students by college ID
export const getStudentsByCollegeId = async (collegeId) => {
  try {
    const college = await getCollegeById(collegeId);
    
    if (!college) {
      return [];
    }

    return college.assignedStudents || [];
  } catch (error) {
    console.error("Error fetching students by college:", error);
    return [];
  }
};

// Search colleges
export const searchColleges = async (searchTerm) => {
  try {
    const allColleges = await getAllColleges();
    
    const filteredColleges = allColleges.filter((college) =>
      college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filteredColleges;
  } catch (error) {
    console.error("Error searching colleges:", error);
    return [];
  }
};

// Get college statistics
export const getCollegeStatistics = async () => {
  try {
    const colleges = await getAllColleges(true); // Include inactive

    const stats = {
      totalColleges: colleges.length,
      activeColleges: colleges.filter(c => c.status === COLLEGE_STATUS.ACTIVE).length,
      inactiveColleges: colleges.filter(c => c.status === COLLEGE_STATUS.INACTIVE).length,
      pendingColleges: colleges.filter(c => c.status === COLLEGE_STATUS.PENDING).length,
      totalStudentsAssigned: 0,
      totalInternsActive: 0,
      averageStudentsPerCollege: 0,
      collegesByAgreementType: {
        [AGREEMENT_TYPES.MOU]: 0,
        [AGREEMENT_TYPES.CONTRACT]: 0,
        [AGREEMENT_TYPES.INFORMAL]: 0,
        [AGREEMENT_TYPES.PARTNERSHIP]: 0,
      },
      collegesByInternshipType: {
        [INTERNSHIP_TYPES.FULL_TIME]: 0,
        [INTERNSHIP_TYPES.PART_TIME]: 0,
        [INTERNSHIP_TYPES.REMOTE]: 0,
        [INTERNSHIP_TYPES.ON_SITE]: 0,
        [INTERNSHIP_TYPES.HYBRID]: 0,
      },
    };

    colleges.forEach((college) => {
      stats.totalStudentsAssigned += college.studentsCount || 0;
      stats.totalInternsActive += college.internCount || 0;

      // Count by agreement type
      if (college.agreementType) {
        stats.collegesByAgreementType[college.agreementType]++;
      }

      // Count by internship type
      if (college.internshipTypes && Array.isArray(college.internshipTypes)) {
        college.internshipTypes.forEach((type) => {
          if (stats.collegesByInternshipType[type] !== undefined) {
            stats.collegesByInternshipType[type]++;
          }
        });
      }
    });

    if (stats.totalColleges > 0) {
      stats.averageStudentsPerCollege = Math.round(
        stats.totalStudentsAssigned / stats.totalColleges
      );
    }

    return stats;
  } catch (error) {
    console.error("Error getting college statistics:", error);
    return null;
  }
};

// Bulk assign students to college
export const bulkAssignStudentsToCollege = async (collegeId, studentsData) => {
  try {
    const college = await getCollegeById(collegeId);

    if (!college) {
      throw new Error("College not found");
    }

    const existingStudents = college.assignedStudents || [];
    const newAssignments = [];
    const skippedStudents = [];

    studentsData.forEach((studentData) => {
      const isAlreadyAssigned = existingStudents.some(
        (student) => student.userId === studentData.userId
      );

      if (!isAlreadyAssigned) {
        newAssignments.push({
          ...studentData,
          assignedAt: Timestamp.now(),
          status: "active",
        });
      } else {
        skippedStudents.push(studentData.userName);
      }
    });

    if (newAssignments.length === 0) {
      throw new Error("All selected students are already assigned to this college");
    }

    const collegeRef = doc(db, COLLEGES_COLLECTION, collegeId);

    // Add all new assignments
    for (const assignment of newAssignments) {
      await updateDoc(collegeRef, {
        assignedStudents: arrayUnion(assignment),
      });
    }

    // Update counts
    await updateDoc(collegeRef, {
      studentsCount: (college.studentsCount || 0) + newAssignments.length,
      updatedAt: Timestamp.now(),
    });

    let message = `${newAssignments.length} students assigned successfully!`;
    if (skippedStudents.length > 0) {
      message += ` ${skippedStudents.length} students were already assigned.`;
    }

    toast.success(message);
    return {
      assigned: newAssignments.length,
      skipped: skippedStudents.length,
      skippedStudents,
    };
  } catch (error) {
    console.error("Error bulk assigning students:", error);
    toast.error(error.message || "Failed to assign students to college");
    throw error;
  }
};